import { call, put, all, takeLatest, SagaReturnType } from 'redux-saga/effects'
import { StatusCodes } from 'http-status-codes'
// import * as Sentry from '@sentry/node'
import get from 'lodash/get'
import { signOut } from 'next-auth/react'

import updateDefaultlogo from '../../../service/user/update-default-logo/updateDefaultlogo'
import imageDimensions from '../../../util/image-dimensions/imageDimensions'
import uploadLogo from '../../../service/draft/upload-logo/uploadLogo'
import deleteDefaultlogo from '../../../service/user/delete-default-logo/deleteDefaultlogo'
import { getCorrelationIdFromError } from '../../../util/error/getCorrelationIdFromError'
import {
  LOG_OUT,
  UPDATE_INFO,
  UPDATE_SETTINGS,
  UPDATE_DEFAULT_LOGO,
  UPLOAD_DEFAULT_LOGO,
  DELETE_DEFAULT_LOGO,
  UpdateDefaultLogoAction,
  UploadDefaultLogoAction,
  updateUserInfo,
  updateUserSettings,
  setUserInfo,
} from '../../actions/user/user'
import {
  setUserInfoSubmissionStatus,
  setUserSettingsSubmissionStatus,
  setLogoUploadingStatus,
  showDialog,
} from '../../actions/ui/ui'
import updateRecruiterInfo from '../../../service/user/update-recruiter-info/updateRecruiterInfo'
import type { UserContactInfoOutgoing, UserConsentOutgoing } from '../../reducers/user/user'
import type { DashboardUser } from '../../../schema/user'
import { getConfig } from '../../../service/util/config/config'

export const outgoingUserInfo = (user: Partial<DashboardUser>): UserContactInfoOutgoing => {
  const { title, firstName, lastName, phoneNumber } = user

  return {
    title: title || null,
    firstName: firstName || null,
    lastName: lastName || null,
    phoneNumber: phoneNumber || null,
  }
}

export function* logUserOutSaga(): Generator<any, void, any> {
  try {
    // After calling oidc logout, callback to Salesforce custom logout page to ensure user is logged out
    const config = getConfig()
    yield call(signOut, {
      callbackUrl: config.SALES_FORCE_LOGOUT_URL,
    })
  } catch (error) {
    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content: 'Uitloggen niet gelukt. Probeer het later opnieuw.',
      }),
    )
    // Sentry.captureException(error)
  }
}

export function* updateUserInfoSaga({ payload: user }: ReturnType<typeof updateUserInfo>): Generator<any, void, any> {
  try {
    yield put(setUserInfoSubmissionStatus(true))
    const updatedUserInfoToSend: UserContactInfoOutgoing = yield call(outgoingUserInfo, user)
    const response = yield call(updateRecruiterInfo, updatedUserInfoToSend)
    if (response.status === StatusCodes.ACCEPTED) {
      yield put(setUserInfo(user))
    } else {
      // Sentry.captureException(
      //   new Error(
      //     `Failed to update recruiter info, expected ${StatusCodes.ACCEPTED} but got ${response.status}`, // eslint-disable-line max-len
      //   ),
      // )
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content: 'Onverwachte serverreactie ontvangen.',
        }),
      )
    }
  } catch (error) {
    // Sentry.captureException(error)
    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content: 'Het is niet gelukt de gegevens op te slaan. Probeer het later opnieuw.',
        correlationId: getCorrelationIdFromError(error),
      }),
    )
  } finally {
    yield put(setUserInfoSubmissionStatus(false))
  }
}

export function* updateUserSettingsSaga({ payload: user }: ReturnType<typeof updateUserSettings>) {
  try {
    yield put(setUserSettingsSubmissionStatus(true))
    const consentData: UserConsentOutgoing = {
      consentForMarketingContact: user.consentedForMarketingContact,
      consentedForPhoneCalls: user.consentedForPhoneCalls,
      consentedForJobExpiryEmail: user.consentedForJobExpiryEmail,
    }
    const response: SagaReturnType<typeof updateRecruiterInfo> = yield call(updateRecruiterInfo, consentData)
    if (response.status === StatusCodes.ACCEPTED) {
      yield put(setUserInfo(user))
    } else {
      // Sentry.captureException(
      //   new Error(`Failed to update recruiter info, expected ${StatusCodes.ACCEPTED} but got ${response.status}`),
      // )
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content: 'Het is niet gelukt de gegevens op te slaan. Probeer het later opnieuw.',
        }),
      )
    }
  } catch (error) {
    // Sentry.captureException(error)
    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content: 'Het is niet gelukt de gegevens op te slaan. Probeer het later opnieuw.',
        correlationId: getCorrelationIdFromError(error),
      }),
    )
  } finally {
    yield put(setUserSettingsSubmissionStatus(false))
  }
}

export const validationError = (message: string): { type: string; message: string } => ({
  type: 'validation',
  message,
})

export const formDataWithLogo = (logo: Blob): FormData => {
  const formData = new FormData()
  formData.append('logo', logo)
  return formData
}

export function* updateDefaultLogoSaga(action: UpdateDefaultLogoAction) {
  try {
    const response = yield call(updateDefaultlogo, action)
    if (response.status !== StatusCodes.ACCEPTED) {
      // Sentry.captureException(
      //   new Error(`Failed to update default logo, expected ${StatusCodes.ACCEPTED} but got ${response.status}`),
      // )
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content: 'Onverwachte serverreactie ontvangen.',
        }),
      )
    } else {
      yield put(
        showDialog({
          title: 'NIEUW LOGO',
          content: 'Het nieuwe logo zal vanaf nu bij nieuw geplaatste vacatures getoond worden.',
        }),
      )
    }
  } catch (error) {
    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content: 'Het is niet gelukt het logo bij te werken. Probeer het later opnieuw.',
        correlationId: getCorrelationIdFromError(error),
      }),
    )
  }
}

export function* deleteDefaultLogoSaga() {
  try {
    const response: SagaReturnType<typeof deleteDefaultlogo> = yield call(deleteDefaultlogo)
    if (response.status !== StatusCodes.ACCEPTED) {
      // Sentry.captureException(
      //   new Error(`Failed to update default logo, expected ${StatusCodes.ACCEPTED} but got ${response.status}`),
      // )
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content: 'Het verwijderen van het logo is mislukt.',
        }),
      )
    }
  } catch (error) {
    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content: 'Het verwijderen van het logo is mislukt.',
        correlationId: getCorrelationIdFromError(error),
      }),
    )
  }
}

export function* uploadDefaultLogoSaga({ payload: { file, setFieldValue, setFieldError } }: UploadDefaultLogoAction) {
  try {
    yield put(setLogoUploadingStatus(false))

    const { type, size } = file

    if (!['image/png', 'image/jpeg'].includes(type)) throw validationError('Alleen PNG en JPG zijn toegestaan')
    if (size > 2097152) {
      throw validationError(
        `Het bestand is ${Math.floor((size / 1048576) * 10) / 10} MB, de grootte moet kleiner dan 2 MB zijn`,
      )
    }
    const { width }: SagaReturnType<typeof imageDimensions> = yield call<any>(imageDimensions, file)
    if (width < 280) {
      throw validationError(`De breedte van de afbeelding is ${width} pixels. Minimale breedte moet 280 pixels zijn.`)
    }

    const formData = yield call(formDataWithLogo, file)
    try {
      yield put(setLogoUploadingStatus(true))
      const response = yield call(uploadLogo, formData)
      if (get(response, 'status') === StatusCodes.OK && response.data) {
        const { location: key } = response.data
        yield call<any>(setFieldValue, 'company.defaultLogo.key', key, true)
      } else {
        yield put(
          showDialog({
            title: 'Er ging iets mis',
            content: 'Onverwachte serverreactie ontvangen.',
          }),
        )
      }
    } catch (error) {
      // Sentry.captureException(error)
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content: 'Het uploaden van het logo is mislukt. Probeer het later opnieuw.',
        }),
      )
    }
  } catch (error) {
    if (get(error, 'type') !== 'validation') {
      // Sentry.captureException(error)
    }
    yield call<any>(
      setFieldError,
      'company.defaultLogo.key',
      get(error, 'type') === 'validation' ? error.message : 'Er ging iets mis met het uploaden',
    )
  }
  yield put(setLogoUploadingStatus(false))
}

export default function* saga(): Generator<any, void, void> {
  yield all([
    takeLatest(LOG_OUT, logUserOutSaga),
    takeLatest(UPDATE_INFO, updateUserInfoSaga),
    takeLatest(UPDATE_SETTINGS, updateUserSettingsSaga),
    takeLatest(UPDATE_DEFAULT_LOGO, updateDefaultLogoSaga),
    takeLatest(UPLOAD_DEFAULT_LOGO, uploadDefaultLogoSaga),
    takeLatest(DELETE_DEFAULT_LOGO, deleteDefaultLogoSaga),
  ])
}
